/**
 * components/charts/ScatterMaeMfe — every closed trade as a dot in excursion space: how
 * far it went offside (MAE, x) versus the best it reached (MFE, y), both in R. Dots are
 * coloured by OUTCOME (win / loss / scratch) — a fixed-meaning status split, so the
 * terminal's poles with a named legend, never colour-alone. A y = x reference line marks
 * where the favourable and adverse swings were equal: dots above it ran further in your
 * favour than against you. ≥8px markers with a 2px surface ring so overlaps stay legible;
 * each dot carries a native tooltip with its symbol, setup and realised R.
 */
import type { ScatterPoint } from '../../lib/analytics';

interface ScatterMaeMfeProps {
  readonly points: readonly ScatterPoint[];
  readonly height?: number;
}

const VIEW_W = 720;
const PAD = { l: 42, r: 16, t: 16, b: 34 } as const;

function outcomeColor(result: ScatterPoint['result']): string {
  return result === 'WIN' ? 'var(--profit)' : result === 'LOSS' ? 'var(--loss)' : 'var(--text-muted)';
}

export function ScatterMaeMfe({ points, height = 300 }: ScatterMaeMfeProps): JSX.Element {
  if (points.length === 0) {
    return <div className="state"><span className="muted">NO DATA YET</span></div>;
  }

  const plotW = VIEW_W - PAD.l - PAD.r;
  const plotH = height - PAD.t - PAD.b;
  const max = Math.max(0.5, ...points.flatMap((p) => [p.mae, p.mfe])) * 1.08;

  const x = (v: number): number => PAD.l + (v / max) * plotW;
  const y = (v: number): number => PAD.t + (1 - v / max) * plotH;
  const ticks = [0, max / 2, max];

  return (
    <div className="chart chart--scatter">
      <div className="exc__legend" aria-hidden>
        <span className="exc__key"><span className="exc__swatch" style={{ background: 'var(--profit)' }} /> Win</span>
        <span className="exc__key"><span className="exc__swatch" style={{ background: 'var(--loss)' }} /> Loss</span>
        <span className="exc__key"><span className="exc__swatch" style={{ background: 'var(--text-muted)' }} /> Scratch</span>
      </div>
      <svg viewBox={`0 0 ${VIEW_W} ${height}`} role="img" aria-label="MAE versus MFE by trade outcome" preserveAspectRatio="none">
        {/* grid */}
        {ticks.map((t) => (
          <g key={`gx${t}`}>
            <line x1={x(t)} x2={x(t)} y1={PAD.t} y2={PAD.t + plotH} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={x(t)} y={height - 10} fontSize={11} fill="var(--text-muted)" textAnchor="middle">{t.toFixed(1)}R</text>
          </g>
        ))}
        {ticks.map((t) => (
          <g key={`gy${t}`}>
            <line x1={PAD.l} x2={PAD.l + plotW} y1={y(t)} y2={y(t)} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={PAD.l - 6} y={y(t) + 3} fontSize={11} fill="var(--text-muted)" textAnchor="end">{t.toFixed(1)}R</text>
          </g>
        ))}

        {/* y = x reference (equal adverse/favourable excursion) */}
        <line x1={x(0)} y1={y(0)} x2={x(max)} y2={y(max)} stroke="var(--viz-axis)" strokeWidth={1} strokeDasharray="4 4" />

        {points.map((p, i) => (
          <circle key={i} cx={x(p.mae)} cy={y(p.mfe)} r={5} fill={outcomeColor(p.result)} fillOpacity={0.82} stroke="var(--viz-surface)" strokeWidth={1.5}>
            <title>{`${p.symbol} · ${p.setup}: MAE −${p.mae.toFixed(2)}R, MFE +${p.mfe.toFixed(2)}R → ${p.r >= 0 ? '+' : '−'}${Math.abs(p.r).toFixed(2)}R`}</title>
          </circle>
        ))}

        {/* axis titles */}
        <text x={PAD.l + plotW / 2} y={height - 24} fontSize={11} fill="var(--text-secondary)" textAnchor="middle">Max adverse excursion →</text>
        <text x={14} y={PAD.t + plotH / 2} fontSize={11} fill="var(--text-secondary)" textAnchor="middle" transform={`rotate(-90 14 ${PAD.t + plotH / 2})`}>Max favorable excursion →</text>
      </svg>
    </div>
  );
}
